import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  HStack,
  Progress,
  Spinner,
  Tag,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import React from "react";

import useApi from "../../../shared/hooks/useApi";
import { useSelection } from "../../../shared/hooks/useSelection";
import { PatientId } from "../../../shared/schema/schema";
import BulkScheduleTasksModal from "./components/BulkScheduleTasksModal";
import RnSchedulingMapCanvas from "./components/RnSchedulingMapCanvas";
import RnSchedulingMapFilters from "./components/RnSchedulingMapFilters";
import RnSchedulingMapPatientList from "./components/RnSchedulingMapPatientList";
import {
  RnSchedulingMapFiltersProvider,
  useRnSchedulingMapQueryFilters,
} from "./hooks/useRnSchedulingMapFilters";
import { isMappable } from "./rn-scheduling-map.types";

function RnSchedulingMapPageContent() {
  const { queries } = useApi();
  const filters = useRnSchedulingMapQueryFilters();
  const [selectedPatientId, setSelectedPatientId] = React.useState<PatientId | null>(null);
  const checkedPatients = useSelection<PatientId>();
  const bulkScheduleModal = useDisclosure();

  const patientsQuery = useQuery(queries.rnSchedulingMap.patients(filters));

  const patients = React.useMemo(() => patientsQuery.data ?? [], [patientsQuery.data]);
  const mappablePatients = React.useMemo(() => patients.filter(isMappable), [patients]);
  const unmappableCount = patients.length - mappablePatients.length;
  const requireSchedulingCount = patients.filter(
    (patient) => patient.schedulingStatus === "REQUIRE_SCHEDULING"
  ).length;

  const checkedPatientsInResult = React.useMemo(
    () => patients.filter((patient) => checkedPatients.isSelected(patient.patientId)),
    [patients, checkedPatients]
  );
  const allChecked = patients.length > 0 && checkedPatientsInResult.length === patients.length;

  // Drop the selection when the selected patient falls out of the current result.
  React.useEffect(() => {
    if (
      selectedPatientId !== null &&
      !patients.some((patient) => patient.patientId === selectedPatientId)
    ) {
      setSelectedPatientId(null);
    }
  }, [patients, selectedPatientId]);

  // Same for checked patients: a patient that left the result cannot be bulk scheduled.
  React.useEffect(() => {
    if (patientsQuery.data === undefined) {
      return;
    }

    const visiblePatientIds = new Set(patients.map((patient) => patient.patientId));

    for (const patientId of checkedPatients.state) {
      if (!visiblePatientIds.has(patientId)) {
        checkedPatients.remove(patientId);
      }
    }
  }, [patients, patientsQuery.data, checkedPatients]);

  function handleToggleAll() {
    if (allChecked) {
      checkedPatients.clear();
      return;
    }

    checkedPatients.addMany(patients.map((patient) => patient.patientId));
  }

  return (
    <Flex direction="column" gap={3} height="calc(100vh - 190px)" p={4}>
      <Flex align="center" gap={3} justify="space-between">
        <Heading size="md">RN scheduling map</Heading>
        <HStack spacing={2}>
          <Tag colorScheme="blue">{patients.length} patients</Tag>
          <Tag colorScheme="red">{requireSchedulingCount} require scheduling</Tag>
          {unmappableCount > 0 && (
            <Tag colorScheme="orange">{unmappableCount} without a mapped address</Tag>
          )}
          <Button
            colorScheme="blue"
            isDisabled={checkedPatientsInResult.length === 0}
            size="sm"
            onClick={bulkScheduleModal.onOpen}
          >
            Schedule task for selected patients
            {checkedPatientsInResult.length > 0 ? ` (${checkedPatientsInResult.length})` : ""}
          </Button>
        </HStack>
      </Flex>

      <RnSchedulingMapFilters />

      {patientsQuery.isFetching && !patientsQuery.isPending && (
        <Progress isIndeterminate size="xs" />
      )}

      <Box flex={1} minHeight={0}>
        {(() => {
          switch (patientsQuery.status) {
            case "pending":
              return (
                <Flex align="center" gap={2} height="100%" justify="center">
                  <Spinner size="sm" />
                  <Text>Loading patients...</Text>
                </Flex>
              );

            case "error":
              return (
                <Alert status="error">
                  <AlertIcon />
                  Failed to load the patients for this date range.
                </Alert>
              );

            case "success":
              if (patients.length === 0) {
                return (
                  <Alert status="info">
                    <AlertIcon />
                    No patients have a certification period ending in the selected dates.
                  </Alert>
                );
              }

              return (
                <Flex gap={3} height="100%">
                  <Box
                    bg="white"
                    borderRadius="md"
                    borderWidth="1px"
                    display="flex"
                    flexDirection="column"
                    overflow="hidden"
                    width="320px"
                  >
                    <Flex align="center" borderBottomWidth="1px" gap={2} px={3} py={2}>
                      <Checkbox
                        isChecked={allChecked}
                        isIndeterminate={!allChecked && checkedPatientsInResult.length > 0}
                        size="sm"
                        onChange={handleToggleAll}
                      >
                        <Text color="gray.600" fontSize="xs" fontWeight="semibold">
                          {checkedPatientsInResult.length > 0
                            ? `${checkedPatientsInResult.length} selected`
                            : "Select all"}
                        </Text>
                      </Checkbox>
                      <Text color="gray.600" flex={1} fontSize="xs" textAlign="right">
                        Sorted by cert. period end
                      </Text>
                    </Flex>
                    <Box flex={1} minHeight={0} overflowY="auto">
                      <RnSchedulingMapPatientList
                        isChecked={checkedPatients.isSelected}
                        patients={patients}
                        selectedPatientId={selectedPatientId}
                        onSelectPatient={setSelectedPatientId}
                        onToggleChecked={checkedPatients.toggle}
                      />
                    </Box>
                  </Box>

                  <Box flex={1} minWidth={0}>
                    <RnSchedulingMapCanvas
                      isChecked={checkedPatients.isSelected}
                      patients={mappablePatients}
                      selectedPatientId={selectedPatientId}
                      onCheckPatients={checkedPatients.addMany}
                      onSelectPatient={setSelectedPatientId}
                      onToggleChecked={checkedPatients.toggle}
                    />
                  </Box>
                </Flex>
              );
          }
        })()}
      </Box>

      {bulkScheduleModal.isOpen && (
        <BulkScheduleTasksModal
          isOpen={bulkScheduleModal.isOpen}
          patients={checkedPatientsInResult}
          onClose={bulkScheduleModal.onClose}
          onScheduled={checkedPatients.clear}
        />
      )}
    </Flex>
  );
}

export default function RnSchedulingMapPage() {
  return (
    <RnSchedulingMapFiltersProvider>
      <RnSchedulingMapPageContent />
    </RnSchedulingMapFiltersProvider>
  );
}
